import { useInfiniteQuery } from '@tanstack/react-query';
import { useOrganizationStore } from '@/core/stores/organization.store';
import { tasksService } from '../api/tasks.service';
import { TASK_KEYS } from './queryKeys';
import type { TaskActivityCategory } from '../types';

const PAGE_SIZE = 20;

export function useInfiniteBoardActivity(
  projectId: string,
  boardId: string,
  category?: Exclude<TaskActivityCategory, 'general'>,
) {
  const orgId = useOrganizationStore((s) => s.activeOrganizationId);
  const params = { PageSize: PAGE_SIZE, category };

  const { data, hasNextPage, fetchNextPage, isFetchingNextPage, isLoading, isError } =
    useInfiniteQuery({
      queryKey: TASK_KEYS.boardActivity(orgId ?? '', boardId, params),
      queryFn: ({ pageParam, signal }: { pageParam: number; signal: AbortSignal }) =>
        tasksService.getBoardActivity(projectId, boardId, { ...params, Page: pageParam }, signal),
      initialPageParam: 1,
      getNextPageParam: (lastPage) => (lastPage.hasNextPage ? lastPage.page + 1 : undefined),
      staleTime: 60_000,
      enabled: !!orgId && !!projectId && !!boardId,
    });

  const activities = data?.pages.flatMap((p) => p.items ?? []) ?? [];
  const totalCount = data?.pages[0]?.totalCount ?? 0;

  return {
    activities,
    totalCount,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    isLoading,
    isError,
  };
}
